import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Motion primitives.
 *
 * Two rules govern everything here:
 *
 *  1. Motion carries meaning or it does not ship. Entry is staggered so the
 *     eye is led down the page in reading order; it is not sprinkled for
 *     decoration. Nothing loops, nothing bounces, nothing draws attention to
 *     itself while someone is reading a volume figure.
 *
 *  2. `prefers-reduced-motion` is honoured everywhere. The hook returns the
 *     OS setting, and every component below collapses to a plain render when
 *     it is on — not a faster animation, no animation.
 *
 * Durations sit in the 200–320ms band and use a decelerating curve, so a
 * screen settles rather than snapping.
 */

const EASE = [0.22, 1, 0.36, 1];

/** Container that reveals its children one after the other. */
const Stagger = ({
  children,
  delay = 0,
  step = 0.05,
  className,
  as = 'div',
  ...props
}) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] ?? motion.div;

  if (reduce) {
    const Plain = as;
    return (
      <Plain className={className} {...props}>
        {children}
      </Plain>
    );
  }

  return (
    <Tag
      className={className}
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: step, delayChildren: delay } },
      }}
      {...props}
    >
      {children}
    </Tag>
  );
};
Stagger.displayName = 'Stagger';

/** A single item inside a Stagger — or standalone. */
const RiseIn = ({ children, className, as = 'div', distance = 10, ...props }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] ?? motion.div;

  if (reduce) {
    const Plain = as;
    return (
      <Plain className={className} {...props}>
        {children}
      </Plain>
    );
  }

  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, y: distance },
        show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: EASE } },
      }}
      // Works standalone too, when there is no Stagger parent driving it.
      initial="hidden"
      animate="show"
      {...props}
    >
      {children}
    </Tag>
  );
};
RiseIn.displayName = 'RiseIn';

/**
 * A number that counts up to its value.
 *
 * Reserved for the readouts on pilotage screens: the movement says "this was
 * just measured". It is deliberately absent from tables, where a column of
 * animating figures would be unreadable.
 */
const CountUp = ({ value, duration = 0.6, format, className }) => {
  const reduce = useReducedMotion();
  const numeric = typeof value === 'number' ? value : null;
  const [shown, setShown] = React.useState(reduce || numeric === null ? numeric : 0);

  React.useEffect(() => {
    if (reduce || numeric === null) {
      setShown(numeric);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // Same decelerating feel as the entry curve.
      setShown(Math.round(numeric * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [numeric, duration, reduce]);

  if (numeric === null) return <span className={className}>{value}</span>;
  const out = format ? format(shown) : shown.toLocaleString('fr-FR');
  return <span className={className}>{out}</span>;
};
CountUp.displayName = 'CountUp';

/** Fades and slides content in when it scrolls into view — once. */
const RevealOnScroll = ({ children, className, ...props }) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.35, ease: EASE }}
      {...props}
    >
      {children}
    </motion.div>
  );
};
RevealOnScroll.displayName = 'RevealOnScroll';

export { Stagger, RiseIn, CountUp, RevealOnScroll, EASE };
